import { ElementType, forwardRef, ReactNode, useCallback } from 'react'

import { Button } from 'antd'
import { twMerge } from 'tailwind-merge'

import { Link } from '@redwoodjs/router'

import useDisclosure from 'src/hooks/use-disclosure'
import { renderActionItemAsMenuItem } from 'src/hooks/use-make-action-item'

import { ActionItem } from '../../types'
import HStack from '../Flex/HStack'
import VStack from '../Flex/VStack'
import Icon from '../Icon'
import { IconKey } from '../Icon/util'
import { isInternalLink, LinkActionProps, getLinkProps } from '../Link/util'
import Menu from '../Menu/Menu'
import Text, { T } from '../Text'

type TileBaseProps = {
  children: ReactNode
  className?: string
} & LinkActionProps
type ButtonRef<TComponent extends React.ElementType> =
  React.ComponentPropsWithRef<TComponent>['ref']
const TileBase = forwardRef<HTMLAnchorElement | HTMLButtonElement, TileBaseProps>(
  <TComponent extends ElementType>(
    { children, className: propClassName, ...props }: TileBaseProps,
    ref: ButtonRef<TComponent>
  ) => {
    const className = twMerge(
      'flex w-full cursor-pointer overflow-clip rounded-xl border border-neutral-200 bg-white p-6 outline-offset-2 outline-black transition-shadow duration-100 hover:border-neutral-300 hover:shadow-md focus-visible:outline-2 active:border-neutral-300 active:bg-neutral-50',
      propClassName
    )

    const { rel, target } = getLinkProps(!!props.externalHref)

    if (isInternalLink(props)) {
      return (
        <Link
          className={className}
          ref={ref}
          to={props.internalRoute}
          target={target}
          rel={rel}
          {...props}
        >
          {children}
        </Link>
      )
    }

    if (props.externalHref) {
      return (
        <a
          className={className}
          ref={ref}
          href={props.externalHref}
          target={target}
          rel={rel}
          {...props}
        >
          {children}
        </a>
      )
    }

    return (
      <button className={className} ref={ref} {...props}>
        {children}
      </button>
    )
  }
)

type HeroTileProps = {
  actions?: ActionItem[]
  className?: string
  footer?: ReactNode
  thumbnailImageFit?: 'cover' | 'scale-down'
  thumbnailImageOnContextMenu?: (e: React.MouseEvent<HTMLImageElement>) => void
  thumbnailImageSrc: string
  title: string
} & LinkActionProps
const HeroTile = forwardRef<HTMLAnchorElement | HTMLButtonElement, HeroTileProps>(
  (
    {
      actions,
      className,
      footer,
      thumbnailImageFit = 'cover',
      thumbnailImageOnContextMenu,
      thumbnailImageSrc,
      title,
      ...props
    }: HeroTileProps,
    ref
  ) => {
    const actionMenuState = useDisclosure()

    const handleMenuTriggerClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        actionMenuState.toggle()
      },
      [actionMenuState]
    )

    return (
      <section className="relative">
        <div className="absolute top-4 right-4">
          {!!actions?.length && (
            <Menu isOpen={actionMenuState.isOpen} onClose={actionMenuState.close}>
              <Menu.Trigger>
                <Button
                  icon={<Icon.FaEllipsisVertical />}
                  onClick={handleMenuTriggerClick}
                  aria-label="More actions"
                />
              </Menu.Trigger>
              <Menu.Content>
                {actions.map((action) => renderActionItemAsMenuItem(action))}
              </Menu.Content>
            </Menu>
          )}
        </div>
        <TileBase className={twMerge('h-full p-0', className)} {...props} ref={ref}>
          <VStack align="stretch">
            <T.Body
              className={`p-4 text-left text-xl font-semibold ${actions?.length ? 'pr-14' : ''}`}
            >
              {title}
            </T.Body>
            {thumbnailImageSrc && (
              <img
                src={thumbnailImageSrc}
                alt={title}
                className={`h-48 w-full ${thumbnailImageFit === 'cover' ? 'object-cover' : 'object-scale-down'}`}
                onContextMenu={thumbnailImageOnContextMenu}
              />
            )}
            {footer && <div>{footer}</div>}
          </VStack>
        </TileBase>
      </section>
    )
  }
)

type LinkTileProps = {
  className?: string
  description: ReactNode
  icon?: IconKey
  title: string
} & LinkActionProps
const LinkTile = forwardRef<HTMLAnchorElement | HTMLButtonElement, LinkTileProps>(
  ({ className, description, icon, title, ...props }: LinkTileProps, ref) => {
    return (
      <TileBase className={className} {...props} ref={ref}>
        <div
          className={`grid h-fit w-full ${icon ? 'grid-cols-[auto_1fr]' : 'grid-cols-1'} items-start gap-3`}
        >
          {icon && <Icon icon={icon} className="row-span-2 pt-1 text-lg text-neutral-700" />}
          <Text className="text-left text-xl font-semibold">{title}</Text>
          {typeof description === 'string' ? (
            <T.Body className="text-left text-neutral-600">{description}</T.Body>
          ) : description ? (
            <div className="text-left text-neutral-600">{description}</div>
          ) : null}
        </div>
      </TileBase>
    )
  }
)

type ThumbnailTileProps = {
  className?: string
  thumbnailImageSrc: string
  title: string
} & LinkActionProps
const ThumbnailTile = forwardRef<HTMLAnchorElement | HTMLButtonElement, ThumbnailTileProps>(
  ({ className, thumbnailImageSrc, title, ...props }: ThumbnailTileProps, ref) => {
    return (
      <TileBase className={twMerge('h-full p-4', className)} {...props} ref={ref}>
        <HStack align="center" gap={4}>
          {thumbnailImageSrc && (
            <img src={thumbnailImageSrc} alt={title} className="h-[50px] w-[50px] object-contain" />
          )}
          <T.Body className="text-left text-xl font-semibold">{title}</T.Body>
        </HStack>
      </TileBase>
    )
  }
)

const Tile = Object.assign(React.memo(TileBase), {
  Hero: React.memo(HeroTile),
  Link: React.memo(LinkTile),
  Thumbnail: React.memo(ThumbnailTile),
})

export default Tile
